"use client"

import React, { createContext, useContext, useState, useEffect } from "react"
import { doc, onSnapshot, setDoc, getDoc } from "firebase/firestore"
import { db } from "@/lib/firebase/config"
import { useAuth } from "./auth-context"
import {
  AccessSettings,
  DEFAULT_ROUTE_SETTINGS,
  RouteSettings
} from "@/lib/types/access-control"

interface AccessControlContextType {
  routes: { [path: string]: RouteSettings }
  isLoading: boolean
  updateRouteAccess: (path: string, updates: Partial<RouteSettings>) => Promise<void>
  canAccess: (path: string) => boolean
}

const AccessControlContext = createContext<AccessControlContextType | undefined>(undefined)

export function AccessControlProvider({ children }: { children: React.ReactNode }) {
  const { user, userProfile } = useAuth()
  const [settings, setSettings] = useState<AccessSettings>({
    routes: {}
  })
  const [isLoading, setIsLoading] = useState(true)

  // Initialize settings only if they don't exist
  useEffect(() => {
    const initializeSettings = async () => {
      const settingsDoc = doc(db, "system", "access-settings")
      
      try {
        const docSnap = await getDoc(settingsDoc)
        if (!docSnap.exists()) {
          // Only initialize if document doesn't exist
          const defaultSettings: AccessSettings = {
            routes: DEFAULT_ROUTE_SETTINGS
          }
          await setDoc(settingsDoc, defaultSettings)
        }
      } catch (error) {
        console.error("Error checking/initializing settings:", error)
      }
    }

    initializeSettings()
  }, [])

  // Listen for changes in access controls
  useEffect(() => {
    const unsubscribe = onSnapshot(
      doc(db, "system", "access-settings"),
      (doc) => {
        if (doc.exists()) {
          setSettings(doc.data() as AccessSettings)
        }
        setIsLoading(false)
      },
      (error) => {
        console.error("Error fetching access controls:", error)
        setIsLoading(false)
      }
    )

    return () => unsubscribe()
  }, [])

  // Check if user can access a route (with safe defaults)
  const canAccess = (path: string): boolean => {
    // Admin can access everything
    if (userProfile?.email?.endsWith("@genwave.xyz")) {
      return true
    }

    const route = settings.routes[path]
    
    // Default to public access if no settings found
    if (!route) {
      return true
    }

    // Check if auth is required
    if (route.requireAuth) {
      return !!user
    }

    // Otherwise allow access
    return true
  }

  // Simple update for route authentication setting
  const updateRouteAccess = async (
    path: string,
    updates: Partial<RouteSettings>
  ) => {
    try {
      const settingsDoc = doc(db, "system", "access-settings")
      const docSnap = await getDoc(settingsDoc)
      const currentSettings = docSnap.exists() ? docSnap.data() as AccessSettings : { routes: {} }

      await setDoc(settingsDoc, {
        routes: {
          ...currentSettings.routes,
          [path]: {
            requireAuth: updates.requireAuth ?? currentSettings.routes[path]?.requireAuth ?? false
          }
        }
      })
    } catch (error) {
      console.error("Error updating route access:", error)
      throw error
    }
  }

  return (
    <AccessControlContext.Provider
      value={{
        routes: settings.routes,
        isLoading,
        updateRouteAccess,
        canAccess
      }}
    >
      {children}
    </AccessControlContext.Provider>
  )
}

export function useAccessControl() {
  const context = useContext(AccessControlContext)
  if (context === undefined) {
    throw new Error("useAccessControl must be used within an AccessControlProvider")
  }
  return context
}
